import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Send, User, Bot, Brain, X, AlertCircle, MessageCircle, Cloud, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import { webSocketClient, sendCloudFindingChatMessage, handleStreamingResponse, cancelChatMessage, CloudFix, ThinkingStep } from '@/api/websocket';
import { useAuth } from '@/hooks/useAuth';
import { useChatSessionManager } from '@/hooks/useChatSessionManager';
import { ChatHistoryDropdown } from '@/components/chat/ChatHistoryDropdown';
import { CloudUniqueFinding } from '@/api/client';
import { ThinkingDisplay } from '@/components/chat/ThinkingStepItem';
import { ChatMarkdown } from '@/components/chat/MarkdownRenderer';
import { ChatMessage } from '@/components/chat/chat-utils';

interface CloudFindingChatProps {
  finding: CloudUniqueFinding;
}

export function CloudFindingChat({ finding }: CloudFindingChatProps) {
  const { isAuthenticated } = useAuth();
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState('');

  // Helper to get cloud provider with defensive fallback to 'aws'
  const getCloudProvider = (): 'aws' | 'oci' => {
    return finding.cloud_provider || 'aws';
  };
  const [isLoading, setIsLoading] = useState(false);
  const [isConnected, setIsConnected] = useState(false);
  const [connectionError, setConnectionError] = useState<string | null>(null);
  const scrollAreaRef = useRef<HTMLDivElement>(null);
  const [currentBotMessage, setCurrentBotMessage] = useState<string>('');
  const [isTyping, setIsTyping] = useState(false);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  // Thinking state management
  const [isThinking, setIsThinking] = useState(false);
  const [thinkingText, setThinkingText] = useState<string>('');
  const [thinkingSteps, setThinkingSteps] = useState<ThinkingStep[]>([]);
  const [isThinkingExpanded, setIsThinkingExpanded] = useState(true); // Default to expanded
  const [thinkingComplete, setThinkingComplete] = useState(false);

  // Cancellation state management
  const [currentMessageId, setCurrentMessageId] = useState<string | null>(null);
  const [canCancel, setCanCancel] = useState(false);
  const [waitingForFixes, setWaitingForFixes] = useState(false);
  const waitingForFixesTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Chat history - gate on auth so session bootstrap doesn't run before WebSocket is ready
  const sessionManager = useChatSessionManager(
    { contextType: 'cloud_finding', contextId: finding.id, enabled: !!isAuthenticated },
    setMessages
  );

  // Auto-scroll with user scroll-up detection
  const userWantsToStayUpRef = useRef(false);
  const scrollToBottom = useCallback(() => {
    if (userWantsToStayUpRef.current) return;
    if (scrollAreaRef.current) {
      scrollAreaRef.current.scrollTop = scrollAreaRef.current.scrollHeight;
    }
  }, []);

  useEffect(() => {
    const el = scrollAreaRef.current;
    if (!el) return;
    const markUp = () => { userWantsToStayUpRef.current = true; };
    const checkBottom = () => {
      const dist = el.scrollHeight - el.scrollTop - el.clientHeight;
      if (dist < 50) userWantsToStayUpRef.current = false;
    };
    const onWheel = (e: WheelEvent) => { if (e.deltaY < 0) markUp(); else setTimeout(checkBottom, 50); };
    el.addEventListener('wheel', onWheel, { passive: true });
    return () => el.removeEventListener('wheel', onWheel);
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages, currentBotMessage, isThinking, thinkingText, scrollToBottom]);

  useEffect(() => {
    let isComponentMounted = true;

    if (!isAuthenticated) {
      return;
    }

    const initializeWebSocket = async () => {
      try {
        setConnectionError(null);
        await webSocketClient.connect();
        if (isComponentMounted) {
          setIsConnected(true);
        }
      } catch (error) {
        if (isComponentMounted) {
          console.error('Failed to connect to WebSocket:', error);
          setConnectionError('Failed to connect to chat service. Please try refreshing the page.');
          setIsConnected(false);
        }
      }
    };

    const handleConnect = () => {
      if (isComponentMounted) {
        setIsConnected(true);
        setConnectionError(null);
      }
    };

    const handleDisconnect = () => {
      if (isComponentMounted) {
        setIsConnected(false);
        setConnectionError('Connection lost. Attempting to reconnect...');
      }
    };

    const handleError = (error: Event) => {
      if (isComponentMounted) {
        console.error('WebSocket error:', error);
        setConnectionError('Connection error occurred.');
      }
    };

    webSocketClient.onConnect(handleConnect);
    webSocketClient.onDisconnect(handleDisconnect);
    webSocketClient.onError(handleError);

    initializeWebSocket();

    return () => {
      isComponentMounted = false;
    };
  }, [isAuthenticated]);

  const getSamplePrompts = () => {
    const provider = getCloudProvider();
    return [
      "How do I fix this cloud security issue?",
      provider === 'oci'
        ? "Show me the OCI CLI commands to remediate this"
        : "Show me the AWS CLI commands to remediate this",
      "What's the risk if I don't address this?",
      "Generate Terraform code to fix this"
    ];
  };

  const handleSamplePrompt = (prompt: string) => {
    setInput(prompt);
    setTimeout(() => {
      if (inputRef.current) {
        inputRef.current.focus();
        inputRef.current.style.height = 'auto';
        inputRef.current.style.height = Math.min(inputRef.current.scrollHeight, 120) + 'px';
      }
    }, 50);
  };

  const handleCancel = () => {
    if (currentMessageId && canCancel) {
      cancelChatMessage(currentMessageId);

      // Preserve whatever has been streamed so far
      const partialText = currentBotMessage;
      const partialThinking = thinkingText;
      const partialSteps = thinkingSteps;

      setIsLoading(false);
      setIsTyping(false);
      setIsThinking(false);
      setThinkingComplete(false);
      setThinkingText('');
      setThinkingSteps([]);
      setCurrentBotMessage('');
      if (waitingForFixesTimerRef.current) clearTimeout(waitingForFixesTimerRef.current);
      setWaitingForFixes(false);
      setCanCancel(false);
      setCurrentMessageId(null);

      setMessages((prev) => [...prev, {
        id: `cancelled-${Date.now()}`,
        text: partialText || 'Request cancelled.',
        sender: 'bot',
        timestamp: Date.now(),
        thinking: partialThinking ? {
          text: partialThinking,
          steps: partialSteps,
          isExpanded: false,
        } : undefined,
      }]);
    }
  };

  const handleSend = async () => {
    if (input.trim() === '' || !isConnected || isLoading) return;

    const userMessage: ChatMessage = {
      id: `user-${Date.now()}`,
      text: input.trim(),
      sender: 'user',
      timestamp: Date.now(),
    };

    setMessages((prev) => [...prev, userMessage]);
    const userInput = input.trim();
    setInput('');
    setIsLoading(true);
    setIsTyping(false);
    setCurrentBotMessage('');

    if (inputRef.current) {
      inputRef.current.style.height = 'auto';
      inputRef.current.style.height = '44px';
    }

    // Reset thinking state for new message
    setIsThinking(false);
    setThinkingText('');
    setThinkingSteps([]);
    setThinkingComplete(false);
    setIsThinkingExpanded(true); // Default to expanded during streaming

    setTimeout(() => {
      inputRef.current?.focus();
    }, 50);

    // Set up streaming response handler variables
    let accumulatedMessage = '';
    let accumulatedThinking = '';
    let accumulatedSteps: ThinkingStep[] = [];

    try {
      const messageId = sendCloudFindingChatMessage(
        userInput,
        finding.id,
        finding,
        {
          cloud_provider: getCloudProvider(),
          aws_account_id: finding.aws_account_id,
          aws_connection_id: finding.aws_connection_id,
          oci_tenancy_ocid: finding.oci_tenancy_ocid,
          oci_connection_id: finding.oci_connection_id,
        }
      );
      setCurrentMessageId(messageId);
      setCanCancel(true);

      const botMessageId = `bot-${Date.now()}`;
      let botMessageComplete = false;

      const handleResponse = handleStreamingResponse(
        messageId,
        // onChunk
        (chunk: string) => {
          accumulatedMessage += chunk;
          setCurrentBotMessage(accumulatedMessage);
          setIsTyping(false);
        },
        // onComplete
        () => {
          if (waitingForFixesTimerRef.current) clearTimeout(waitingForFixesTimerRef.current);
          setWaitingForFixes(false);
          setMessages((prev) => [...prev, {
            id: botMessageId,
            text: accumulatedMessage,
            sender: 'bot',
            timestamp: Date.now(),
            thinking: (accumulatedThinking || accumulatedSteps.length > 0) ? {
              text: accumulatedThinking,
              steps: accumulatedSteps,
              isExpanded: false, // Collapse thinking after completion
            } : undefined,
          }]);
          setCurrentBotMessage('');
          setIsLoading(false);
          setIsTyping(false);
          setIsThinking(false);
          setThinkingComplete(false);
          setThinkingText('');
          setThinkingSteps([]);
          setCanCancel(false);
          setCurrentMessageId(null);
          botMessageComplete = true;
          // Clean up the message handler to prevent memory leaks
          webSocketClient.offMessage(handleResponse);
        },
        // onThinking - handle thinking chunks with optional structured steps
        (thinkingChunk: string, steps?: ThinkingStep[]) => {
          accumulatedThinking += thinkingChunk;
          setThinkingText(accumulatedThinking);
          // Accumulate structured steps if provided, upsert by step id to allow status updates
          if (steps && steps.length > 0) {
            const existingStepsMap = new Map(accumulatedSteps.map(s => [s.id, s]));
            let hasChanges = false;

            for (const incomingStep of steps) {
              const existing = existingStepsMap.get(incomingStep.id);
              if (existing) {
                // Merge: preserve existing fields, update with incoming fields
                const merged = { ...existing, ...incomingStep };
                if (JSON.stringify(merged) !== JSON.stringify(existing)) {
                  existingStepsMap.set(incomingStep.id, merged);
                  hasChanges = true;
                }
              } else {
                // New step - append
                existingStepsMap.set(incomingStep.id, incomingStep);
                hasChanges = true;
              }
            }

            if (hasChanges) {
              accumulatedSteps = Array.from(existingStepsMap.values());
              setThinkingSteps(accumulatedSteps);
            }
          }
          setIsThinking(true);
          setIsTyping(false);
        },
        // onThinkingComplete
        () => {
          setThinkingComplete(true);
          setIsTyping(true);
        },
        // onCancelled - server acknowledged cancellation; handleCancel already
        // preserved partial text and reset UI, so just clean up the handler.
        () => {
          botMessageComplete = true;
          webSocketClient.offMessage(handleResponse);
        },
        // onYAMLFixes
        undefined,
        // onError
        undefined,
        // onCloudFixes
        undefined,
        // onTextComplete - text streaming done, fixes may still be loading
        () => {
          const timer = setTimeout(() => setWaitingForFixes(true), 1000);
          waitingForFixesTimerRef.current = timer;
        }
      );

      webSocketClient.onMessage(handleResponse);

      setTimeout(() => {
        if (!botMessageComplete) {
          setMessages((prev) => [...prev, {
            id: botMessageId,
            text: accumulatedMessage || 'Sorry, I encountered an error processing your request. Please try again.',
            sender: 'bot',
            timestamp: Date.now(),
            thinking: (accumulatedThinking || accumulatedSteps.length > 0) ? {
              text: accumulatedThinking,
              steps: accumulatedSteps,
              isExpanded: false,
            } : undefined,
          }]);
          setCurrentBotMessage('');
          setIsLoading(false);
          setIsTyping(false);
          setThinkingSteps([]);
          setCanCancel(false);
          setCurrentMessageId(null);
          // Clean up the message handler on timeout
          webSocketClient.offMessage(handleResponse);
        }
      }, 300000); // 5 mins timeout (matches backend context timeout for large IaC queries)

    } catch (error) {
      console.error('Failed to send cloud finding message:', error);
      setMessages((prev) => [...prev, {
        id: `error-${Date.now()}`,
        text: 'Sorry, I encountered an error. Please try again.',
        sender: 'bot',
        timestamp: Date.now(),
        thinking: (accumulatedThinking || accumulatedSteps.length > 0) ? {
          text: accumulatedThinking,
          steps: accumulatedSteps,
          isExpanded: false,
        } : undefined,
      }]);
      setIsLoading(false);
      setIsTyping(false);
      setThinkingSteps([]);
      setCanCancel(false);
      setCurrentMessageId(null);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      if (e.shiftKey) {
        return;
      } else {
        e.preventDefault();
        handleSend();
      }
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setInput(e.target.value);
    const textarea = e.target;
    textarea.style.height = 'auto';
    textarea.style.height = Math.min(textarea.scrollHeight, 120) + 'px';
  };

  return (
    <div className="flex flex-col h-full bg-gradient-to-br from-background to-muted/30">
      {/* Header */}
      <div className="p-4 border-b border-border/50">
        <div className="flex items-center gap-3">
          <div className="relative">
            <div className="absolute inset-0 bg-muted rounded-full blur-sm"></div>
            <Cloud className="relative h-6 w-6 text-foreground" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-lg font-semibold text-foreground">
              AI Remediation Assistant
            </h3>
            <div className="flex items-center gap-2 mt-0.5">
              {isConnected ? (
                <>
                  <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                  <span className="text-xs text-green-600 dark:text-green-400 font-medium">
                    Connected
                  </span>
                </>
              ) : (
                <>
                  <div className="w-2 h-2 bg-red-500 rounded-full"></div>
                  <span className="text-xs text-red-600 dark:text-red-400 font-medium">
                    Disconnected
                  </span>
                </>
              )}
            </div>
          </div>
          {sessionManager.enabled && (
            <ChatHistoryDropdown
              sessions={sessionManager.chatHistory.sessions}
              currentSessionId={sessionManager.chatHistory.currentSessionId}
              loading={sessionManager.chatHistory.loading}
              showHistory={sessionManager.showHistory}
              setShowHistory={sessionManager.setShowHistory}
              historyRef={sessionManager.historyRef}
              onLoadSession={sessionManager.handleLoadSession}
              onNewChat={sessionManager.handleNewChat}
              formatSessionDate={sessionManager.formatSessionDate}
              disabled={isLoading || canCancel}
            />
          )}
        </div>
      </div>

      {/* Messages Area */}
      <div
        ref={scrollAreaRef}
        className="flex-1 overflow-y-auto px-4 py-4 space-y-4 scroll-smooth"
      >
        {connectionError && (
          <div className="animate-in slide-in-from-top-2 duration-300">
            <div className="flex items-center gap-3 p-3 bg-red-50/80 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl backdrop-blur-sm">
              <AlertCircle className="h-4 w-4 text-red-500 flex-shrink-0" />
              <p className="text-sm text-red-700 dark:text-red-300 font-medium">{connectionError}</p>
            </div>
          </div>
        )}

        {messages.length === 0 && !isLoading && !connectionError ? (
          <div className="flex flex-col items-center justify-center text-center animate-in fade-in-50 duration-700 py-8">
            <div className="relative mb-4">
              <div className="absolute inset-0 bg-muted rounded-full blur-xl"></div>
              <div className="relative p-4 bg-gradient-to-br from-muted to-muted/50 rounded-full border border-border/50">
                <MessageCircle className="h-8 w-8 text-foreground" />
              </div>
            </div>
            <h4 className="text-lg font-semibold text-foreground mb-2">
              Ask me about this cloud finding
            </h4>
            <p className="text-muted-foreground text-sm mb-6 max-w-sm leading-relaxed">
              I can help you understand how to fix this {getCloudProvider() === 'oci' ? 'OCI' : 'AWS'} security finding and provide specific remediation steps.
            </p>
            <div className="grid grid-cols-1 gap-2 w-full max-w-md">
              {getSamplePrompts().map((prompt, index) => (
                <button
                  key={prompt}
                  onClick={() => handleSamplePrompt(prompt)}
                  disabled={!isConnected}
                  className={cn(
                    "p-3 bg-card/50 backdrop-blur-sm border border-border/50 rounded-lg text-left",
                    "hover:bg-card/80 hover:border-border hover:shadow-md",
                    "transition-all duration-300 text-sm leading-relaxed",
                    "disabled:opacity-50 disabled:cursor-not-allowed",
                    "animate-in slide-in-from-top-4 duration-500"
                  )}
                  style={{ animationDelay: `${index * 100}ms` }}
                >
                  <div className="flex items-start gap-2">
                    <MessageCircle className="h-3 w-3 text-foreground mt-0.5 flex-shrink-0" />
                    <span className="text-xs">{prompt}</span>
                  </div>
                </button>
              ))}
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            {(() => {
              const completedMessages = isLoading && messages.length > 0 && messages[messages.length - 1].sender === 'user'
                ? messages.slice(0, -1)
                : messages;

              return completedMessages.map((message) => {
                if (message.sender === 'user') {
                  return (
                    <div key={message.id} className={cn(
                      "animate-in slide-in-from-bottom-2 duration-500 flex gap-3 justify-end"
                    )}>
                      <div className="max-w-[85%]">
                        <div className="rounded-2xl px-3 py-2 shadow-sm bg-primary text-primary-foreground">
                          <p className="text-sm leading-relaxed whitespace-pre-wrap">
                            {message.text}
                          </p>
                        </div>
                      </div>
                      <div className="flex-shrink-0 w-7 h-7 rounded-full bg-gradient-to-br from-blue-500/20 to-blue-500/10 border border-blue-500/20 flex items-center justify-center">
                        <User className="h-3 w-3 text-blue-500" />
                      </div>
                    </div>
                  );
                } else {
                  return (
                    <div key={message.id} className={cn(
                      "animate-in slide-in-from-bottom-2 duration-500 flex gap-3 justify-start"
                    )}>
                      <div className="flex-shrink-0 w-7 h-7 rounded-full bg-gradient-to-br from-muted to-muted/50 border border-border/50 flex items-center justify-center">
                        <Bot className="h-3 w-3 text-foreground" />
                      </div>
                      <div className="max-w-[85%] space-y-2">
                        {message.thinking && (
                          <ThinkingDisplay
                            thinking={message.thinking}
                            messageId={message.id}
                          />
                        )}

                        <div className="rounded-2xl px-3 py-2 bg-card/80 backdrop-blur-sm border border-border/50 shadow-sm">
                          <div className="text-sm leading-relaxed break-words prose-chat">
                            <ChatMarkdown text={message.text} />
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                }
              });
            })()}

            {/* Active conversation */}
            {isLoading && (
              <div className="space-y-3">
                {messages.length > 0 && messages[messages.length - 1].sender === 'user' && (
                  <div className="animate-in slide-in-from-bottom-2 duration-300 flex gap-3 justify-end">
                    <div className="max-w-[85%]">
                      <div className="rounded-2xl px-3 py-2 shadow-sm bg-primary text-primary-foreground">
                        <p className="text-sm leading-relaxed whitespace-pre-wrap">
                          {messages[messages.length - 1].text}
                        </p>
                      </div>
                    </div>
                    <div className="flex-shrink-0 w-7 h-7 rounded-full bg-gradient-to-br from-blue-500/20 to-blue-500/10 border border-blue-500/20 flex items-center justify-center">
                      <User className="h-3 w-3 text-blue-500" />
                    </div>
                  </div>
                )}

                {/* Waiting Indicator - shown while waiting for backend to start */}
                {!isThinking && !isTyping && !currentBotMessage && (
                  <div className="animate-in slide-in-from-bottom-2 duration-300 flex gap-3 justify-start">
                    <div className="flex-shrink-0 w-7 h-7 rounded-full bg-gradient-to-br from-muted to-muted/50 border border-border/50 flex items-center justify-center">
                      <Loader2 className="h-3 w-3 text-foreground animate-spin" />
                    </div>
                    <div className="rounded-2xl px-3 py-2 bg-card/80 backdrop-blur-sm border border-border/50 shadow-sm">
                      <p className="text-xs text-muted-foreground">Starting analysis...</p>
                    </div>
                  </div>
                )}

                {/* Thinking Indicator */}
                {isThinking && (
                  <div className="animate-in slide-in-from-bottom-2 duration-300 flex gap-3 justify-start">
                    <div className="flex-shrink-0 w-7 h-7 rounded-full bg-muted border border-border/50 flex items-center justify-center relative overflow-hidden">
                      <div className="absolute inset-0 " style={{animationDuration: '2s'}}></div>
                      <Brain className="h-3 w-3 text-foreground relative z-10 animate-pulse" />
                    </div>
                    <div className="max-w-[85%]">
                      <ThinkingDisplay
                        thinking={{ text: thinkingText, steps: thinkingSteps, isExpanded: isThinkingExpanded }}
                        messageId="streaming"
                        isStreaming={!thinkingComplete}
                      />
                    </div>
                  </div>
                )}

                {/* Typing Indicator */}
                {isTyping && !currentBotMessage && (
                  <div className="animate-in slide-in-from-bottom-2 duration-300 flex gap-3 justify-start">
                    <div className="flex-shrink-0 w-7 h-7 rounded-full bg-gradient-to-br from-muted to-muted/50 border border-border/50 flex items-center justify-center">
                      <Bot className="h-3 w-3 text-foreground" />
                    </div>
                    <div className="rounded-2xl px-3 py-2 bg-card/80 backdrop-blur-sm border border-border/50 shadow-sm">
                      <div className="flex items-center space-x-1">
                        <span className="h-1.5 w-1.5 bg-muted-foreground/60 rounded-full animate-bounce [animation-delay:-0.3s]"></span>
                        <span className="h-1.5 w-1.5 bg-muted-foreground/60 rounded-full animate-bounce [animation-delay:-0.15s]"></span>
                        <span className="h-1.5 w-1.5 bg-muted-foreground/60 rounded-full animate-bounce"></span>
                      </div>
                    </div>
                  </div>
                )}

                {/* Streaming Bot Message */}
                {currentBotMessage && (
                  <div className="animate-in slide-in-from-bottom-2 duration-300 flex gap-3 justify-start">
                    <div className="flex-shrink-0 w-7 h-7 rounded-full bg-gradient-to-br from-muted to-muted/50 border border-border/50 flex items-center justify-center">
                      <Bot className="h-3 w-3 text-foreground" />
                    </div>
                    <div className="max-w-[85%] rounded-2xl px-3 py-2 bg-card/80 backdrop-blur-sm border border-border/50 shadow-sm">
                      <div className="text-sm leading-relaxed break-words prose-chat">
                        <ChatMarkdown text={currentBotMessage} />
                      </div>

                      {waitingForFixes && (
                        <div className="flex items-center gap-2 mt-3 pt-2 border-t border-border/30">
                          <Loader2 className="h-3.5 w-3.5 text-muted-foreground animate-spin" />
                          <span className="text-xs text-muted-foreground">Loading configuration fixes</span>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Input Area */}
      <div className="border-t border-border/50 p-2">
        <div className="relative flex items-end gap-2 p-2 bg-card/50 backdrop-blur-sm border border-border/50 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300">
          <div className="flex-1 min-h-[40px] max-h-24">
            <textarea
              ref={inputRef}
              value={input}
              onChange={handleInputChange}
              onKeyDown={handleKeyDown}
              placeholder={isConnected ? "Ask about this cloud finding..." : "Connecting..."}
              className={cn(
                "w-full border-0 bg-transparent resize-none text-sm leading-relaxed",
                "focus-visible:ring-0 focus-visible:ring-offset-0 focus:outline-none placeholder:text-muted-foreground/60",
                "min-h-[40px] max-h-24 py-2 px-3 overflow-y-auto"
              )}
              disabled={isLoading || !isConnected}
            />
          </div>

          <div className="flex-shrink-0 self-center">
            <Button
              onClick={canCancel ? handleCancel : handleSend}
              disabled={(!input.trim() && !canCancel) || !isConnected}
              size="icon"
              className={cn(
                "h-9 w-9 rounded-lg transition-all duration-200",
                "disabled:opacity-50 disabled:cursor-not-allowed",
                canCancel
                  ? "bg-red-600 hover:bg-red-700 text-white"
                  : "bg-foreground hover:bg-foreground/90 text-background",
                (!input.trim() && !canCancel) || (!isConnected && !canCancel)
                  ? "scale-95 opacity-50"
                  : "scale-100 hover:scale-105 shadow-lg"
              )}
              title={canCancel ? "Stop request" : "Send message"}
            >
              {canCancel ? (
                <X className="h-4 w-4" />
              ) : isLoading ? (
                <div className="h-3 w-3 animate-spin rounded-full border-2 border-primary-foreground border-t-transparent" />
              ) : (
                <Send className="h-3 w-3" />
              )}
            </Button>
          </div>
        </div>

        <p className="text-xs text-center text-muted-foreground/80 mt-1 px-1">
          AI can make mistakes. Please verify remediation steps before applying to production.
        </p>
      </div>
    </div>
  );
}
